/*
 * Registered for a built bundle only. Under `npm start` the worker's asset cache would answer with
 * the module webpack has just replaced, so hot reload would appear to do nothing — and worse, a
 * worker registered once on localhost outlives the dev server and keeps serving a stale app to
 * every other project on the same port.
 *
 * The rest of this file is about a second thing, and it is worth naming: **an app kept open never
 * finds out there is a new one.** `register` runs once, on load, and a browser checks a worker of
 * its own accord only on a navigation or once every 24 hours — a game left on a phone's home
 * screen is neither, and the player went on playing the bundle they installed. So the page asks
 * (`registration.update()`) whenever it comes back to the front and on a timer while it is there,
 * and reports through `subscribeToUpdates` when a new worker has actually taken over.
 */

// Fifteen minutes while the app is on screen. It is a conditional GET of one small file, so the
// cost of asking is a 304; the cost of not asking is a player on last week's rules.
export const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000

const subscribers = new Set()

/* The seam the app reads an update through — `UpdateBanner` is its one subscriber today. Returns
   the unsubscribe, so a component can hand it straight back out of `useEffect`. */
export const subscribeToUpdates = (callback) => {
  subscribers.add(callback)
  return () => subscribers.delete(callback)
}

const announceNewVersion = () => {
  subscribers.forEach((callback) => callback())
}

/* Which build this page is running, off the shell's own meta tag (stamped per build by
   frontend/scripts/stamp-build.js). It is how the page can tell "reload for the new version" from
   "you already reloaded and this is still you". */
export const currentBuildId = () => {
  if (typeof document === "undefined") return null
  return document.querySelector('meta[name="build-id"]')?.getAttribute("content") ?? null
}

/* `controllerchange` fires in every open page the moment a new worker claims it, which this worker
   does on activate — so it is the one honest signal that the app on disk has moved on.
 *
 * The first one after a *first-ever* install is not an update: the page loaded uncontrolled, the
 * worker installed and claimed it, and telling the player a new version is ready thirty seconds
 * after they opened the site for the first time would be nonsense. Whether the page was already
 * controlled when we started watching is what tells the two apart. */
export const watchForUpdates = (container) => {
  if (!container) return
  let controlled = Boolean(container.controller)
  container.addEventListener("controllerchange", () => {
    if (!controlled) {
      controlled = true
      return
    }
    announceNewVersion()
  })
}

/* Ask the browser to re-fetch the worker: when the app comes back to the front, and on a timer
   while it is there. Nothing runs while the document is hidden — a backgrounded tab is exactly
   where a timer should not be, and the visibility handler checks the moment it returns anyway. */
export const askForUpdates = (registration, { doc = document } = {}) => {
  let timer = null

  const check = () => {
    try {
      Promise.resolve(registration.update()).catch(() => undefined)
    } catch (error) {
      // Offline, or a registration the browser has since dropped. Nothing to do and nothing to
      // say: the next check is fifteen minutes away.
    }
  }

  const stop = () => {
    if (timer == null) return
    clearInterval(timer)
    timer = null
  }

  const start = () => {
    if (timer == null) timer = setInterval(check, UPDATE_CHECK_INTERVAL_MS)
  }

  doc.addEventListener("visibilitychange", () => {
    if (doc.visibilityState === "hidden") {
      stop()
      return
    }
    check()
    start()
  })

  if (doc.visibilityState !== "hidden") start()

  return { check, stop }
}

export const registerServiceWorker = () => {
  if (process.env.NODE_ENV !== "production") return
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return

  // Before the registration resolves, and before `load`: a worker installed on a previous visit
  // can claim this page at any moment, and the handover is what we are here to hear.
  watchForUpdates(navigator.serviceWorker)

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(`${process.env.PUBLIC_URL || ""}/sw.js`)
      .then((registration) => askForUpdates(registration))
      .catch((error) => {
        // A failed registration costs the install prompt and the offline shell, nothing else. The
        // game itself is unaffected, so this must never reach the player as an error.
        console.warn("Service worker registration failed", error)
      })
  })
}

export default registerServiceWorker
